import {A} from "@solidjs/router";
import Container from "~/components/Container";
import Page from "~/components/Page";
import Section from "~/components/Section";

export default function Terms() {
    return (
        <Page metaTitle="Terms & Conditions"
              metaDescription="Understand the terms for using Graceful Space Yoga's website and yoga services. Review our policies on class booking, payments, cancellations, and your responsibilities to ensure a safe and respectful experience">
            <Container>
                <Section>
                    <h1>Terms & Conditions</h1>
                    <p>Last Updated: October 13, 2025</p>
                </Section>
                <Section id="agreement">
                    <h2>Agreement to Terms</h2>
                    <p>
                        These Terms and Conditions ("Terms") govern your access to and use of the Graceful Space Yoga
                        website and booking system located at gracefulspaceyoga.com (the "Site") and the services we
                        provide, including booking and attending Vinyasa flow yoga classes at the local village hall
                        (the "Services"). By creating an account, purchasing class passes, booking a class, or otherwise
                        using the Site, you agree to be bound by these Terms and our Privacy Policy. If you do not
                        agree,
                        do not use the Site or Services.
                    </p>
                </Section>
                <Section>
                    <h2>Table of Contents</h2>
                    <ol>
                        <li><A href="#about">About Graceful Space Yoga</A></li>
                        <li><A href="#eligibility">Eligibility</A></li>
                        <li><A href="#accounts">Accounts and Security</A></li>
                        <li><A href="#class-passes">Class Passes, Pricing, and Payments</A></li>
                        <li><A href="#booking">Booking Policy</A></li>
                        <li><A href="#cancellations">Cancellations and No‑Show Policy</A></li>
                        <li><A href="#refunds">Refunds and Credits</A></li>
                        <li><A href="#schedule">Schedule Changes and Class Cancellations by Us</A></li>
                        <li><A href="#health">Health, Safety, and Participant Responsibilities</A></li>
                        <li><A href="#conduct">Code of Conduct</A></li>
                        <li><A href="#location">Venue, Arrivals, and Late Entry</A></li>
                        <li><A href="#intellectual-property">Intellectual Property</A></li>
                        <li><A href="#privacy">Privacy</A></li>
                        <li><A href="#liability">Disclaimers and Limitation of Liability</A></li>
                        <li><A href="#indemnity">Indemnity</A></li>
                        <li><A href="#force-majeure">Force Majeure</A></li>
                        <li><A href="#changes">Changes to the Services and Terms</A></li>
                        <li><A href="#contact">Contact Information</A></li>
                        <li><A href="#governing-law">Governing Law</A></li>
                    </ol>
                </Section>
                <Section id="about">
                    <h2>1. About Graceful Space Yoga</h2>
                    <p>
                        Graceful Space Yoga ("we", "us", "our") operates a yoga booking system and provides in‑person
                        Vinyasa flow yoga classes at the local village hall. Our Site facilitates account creation,
                        purchase of class passes, and booking of classes.
                    </p>
                </Section>
                <Section id="eligibility">
                    <h2>2. Eligibility</h2>
                    <p>
                        You must be at least 18 years old to attend classes. Participation is not permitted for
                        individuals who are currently pregnant or less than 12 weeks post-partum.
                    </p>
                </Section>
                <Section id="accounts">
                    <h2>3. Accounts and Security</h2>
                    <p>
                        You are responsible for maintaining the confidentiality of your account credentials and for all
                        activities under your account. Notify us immediately of any unauthorised access. We may suspend
                        or terminate accounts that violate these Terms.
                    </p>
                </Section>
                <Section id="class-passes">
                    <h2>4. Class Passes, Pricing, and Payments</h2>
                    <p>
                        - Customers may purchase "Class Passes" which credit their account and can be redeemed to book
                        classes.
                        Class Passes have no cash value, are non‑transferable, and are for personal use only.
                    </p>
                    <p>
                        - Prices, pass types, and inclusions are listed on the Site and may change at any time. The
                        price
                        at purchase time applies to that purchase only.
                    </p>
                    <p>
                        - Payment is taken at the time of purchase via our payment processor. By submitting a payment,
                        you authorise the processor to charge your selected payment method.
                    </p>
                    <p>
                        - We operate a no refunds policy (see Section 7).
                    </p>
                    <p>
                        - Where applicable, expiration dates, pass usage limits, or blackout dates will be stated on the
                        pass description at checkout.
                    </p>
                </Section>
                <Section id="booking">
                    <h2>5. Booking Policy</h2>
                    <p>
                        - A valid Class Pass is required to book a class. One pass is deducted per booked class per
                        person.
                    </p>
                    <p>
                        - Bookings are confirmed only when displayed in your account. We may limit class capacity and
                        manage waitlists at our discretion.
                    </p>
                    <p>
                        - If a waitlist is used and a spot opens, our system may notify you. You are responsible for
                        checking your status and notifications.
                    </p>
                </Section>
                <Section id="cancellations">
                    <h2>6. Cancellations and No‑Show Policy</h2>
                    <p>
                        - Members must cancel using our system at least 12 hours before the class start time for the
                        Class
                        Pass to be returned to their account.
                    </p>
                    <p>
                        - Late cancellations (within 12 hours of the class start) and no‑shows will result in the Class
                        Pass being forfeited.
                    </p>
                    <p>
                        - Cancellations are valid only when completed through the Site; messages via email or social
                        media
                        do not constitute cancellation.
                    </p>
                </Section>
                <Section id="refunds">
                    <h2>7. Refunds and Credits</h2>
                    <p>
                        - No refunds: All purchases of Class Passes are final and non‑refundable, except where required
                        by law.
                    </p>
                    <p>
                        - Pass returns for timely cancellations are handled automatically per Section 6.
                    </p>
                    <p>
                        - We are not responsible for unused or expired passes.
                    </p>
                </Section>
                <Section id="schedule">
                    <h2>8. Schedule Changes and Class Cancellations by Us</h2>
                    <p>
                        We may modify schedules, instructors, class styles, venues, or cancel classes at any time. If we
                        cancel a class you have booked, the Class Pass used will be returned to your account.
                    </p>
                </Section>
                <Section id="health">
                    <h2>9. Health, Safety, and Participant Responsibilities</h2>
                    <p>
                        - You acknowledge that yoga involves physical activity with inherent risks. Participate within
                        your
                        limits and inform the instructor of any injuries, conditions, or pregnancy before class.
                    </p>
                    <p>
                        - Consult a medical professional before beginning any new exercise program. You are responsible
                        for your own health and safety.
                    </p>
                    <p>
                        - Follow all instructor guidance and venue safety rules. We may refuse participation if we deem
                        it
                        unsafe or disruptive.
                    </p>
                </Section>
                <Section id="conduct">
                    <h2>10. Code of Conduct</h2>
                    <p>
                        Be respectful of instructors and participants. No harassment, abuse, or discriminatory
                        behaviour.
                        Appropriate attire and personal hygiene are required. We may remove any person who violates this
                        policy without refund or credit.
                    </p>
                </Section>
                <Section id="location">
                    <h2>11. Venue, Arrivals, and Late Entry</h2>
                    <p>
                        Classes are held at the local village hall as listed on the Site. Please arrive at least 5
                        minutes
                        early to set up. Late entry may not be permitted once class has begun to ensure safety and
                        minimal
                        disruption; if late entry is refused, the pass is forfeited.
                    </p>
                </Section>
                <Section id="intellectual-property">
                    <h2>12. Intellectual Property</h2>
                    <p>
                        The Site and all content, logos, graphics, and materials are owned by or licensed to Graceful
                        Space
                        Yoga and are protected by applicable intellectual property laws. You may not copy, distribute,
                        modify, or create derivative works without our prior written consent.
                    </p>
                </Section>
                <Section id="privacy">
                    <h2>13. Privacy</h2>
                    <p>
                        Your use of the Site is subject to our Privacy Policy, which explains how we collect, use, and
                        protect your personal data. Please review it at <A href="/legal/privacy">/legal/privacy</A>.
                    </p>
                </Section>
                <Section id="liability">
                    <h2>14. Disclaimers and Limitation of Liability</h2>
                    <p>
                        To the maximum extent permitted by law, the Services are provided "as is" and "as available". We
                        make no warranties, express or implied, regarding fitness for a particular purpose,
                        availability,
                        or accuracy. Graceful Space Yoga shall not be liable for any indirect, incidental, special, or
                        consequential damages, or for loss of profits, data, or goodwill. Our total liability for any
                        claim relating to the Services shall not exceed the total fees you paid to us for the Class Pass
                        involved in the claim.
                    </p>
                </Section>
                <Section id="indemnity">
                    <h2>15. Indemnity</h2>
                    <p>
                        You agree to indemnify and hold harmless Graceful Space Yoga, its owners, instructors, and
                        agents
                        from and against any claims, liabilities, damages, losses, and expenses (including reasonable
                        legal fees) arising out of or related to your use of the Site or Services, your violation of
                        these
                        Terms, or your participation in classes.
                    </p>
                </Section>
                <Section id="force-majeure">
                    <h2>16. Force Majeure</h2>
                    <p>
                        We are not responsible for any failure or delay to perform our obligations due to events beyond
                        our reasonable control, including but not limited to severe weather, power outages, strikes,
                        lockouts, fire, flood, epidemics, government actions, or venue unavailability.
                    </p>
                </Section>
                <Section id="changes">
                    <h2>17. Changes to the Services and Terms</h2>
                    <p>
                        We may update the Site, Services, pricing, and these Terms from time to time. Changes take
                        effect
                        when posted on the Site with an updated "Last Updated" date. Continued use of the Site or
                        Services
                        after changes constitute acceptance of the updated Terms.
                    </p>
                </Section>
                <Section id="contact">
                    <h2>18. Contact Information</h2>
                    <p>
                        Graceful Space Yoga<br/>
                        Website: gracefulspaceyoga.com<br/>
                        For inquiries about bookings, passes, or these Terms, please contact us via the contact details
                        provided on the Site.
                    </p>
                </Section>
                <Section id="governing-law">
                    <h2>19. Governing Law</h2>
                    <p>
                        These Terms are governed by and construed in accordance with the laws applicable in England and
                        Wales. You agree that the courts of England and Wales shall have exclusive jurisdiction over any
                        dispute arising out of or relating to these Terms or the Services.
                    </p>
                </Section>
            </Container>
        </Page>
    )
}